import { useEffect, useMemo, useState } from "react";
import CommonCollapsible from "../../common/CommonCollapsible";

const FIELDS = [
  { title: "Title", key: "title", get: (p) => p?.title },
  { title: "First name", key: "firstName", get: (p) => p?.firstName },
  { title: "Middle name", key: "middleName", get: (p) => p?.middleName },
  { title: "Last name", key: "lastName", get: (p) => p?.lastName },
  { title: "Gender", key: "gender", get: (p) => p?.gender },
  { title: "Birth date", key: "birthDate", get: (p) => p?.birthDate },
  {
    title: "Nationality",
    key: "nationality",
    get: (p) => p?.document?.nationality,
  },
  {
    title: "Passport / ID",
    key: "documentNumber",
    get: (p) => p?.document?.number,
  },
  {
    title: "Doc expiry",
    key: "documentExpiry",
    get: (p) => p?.document?.expiryDate,
  },
];

function rowsFor(previous, newData) {
  return FIELDS.map((f) => {
    const before = f.get(previous) || "—";
    const after = f.get(newData) || "—";
    const changed = String(before) !== String(after);
    return { ...f, before, after, changed };
  });
}

function passengerLabel(person, fallbackIndex) {
  const name = [person?.title, person?.firstName, person?.middleName, person?.lastName]
    .filter(Boolean)
    .join(" ");
  return name || `Passenger ${fallbackIndex + 1}`;
}

/** Build previous/new pairs for every passenger on a details-change request. */
export function buildComparePassengers(data) {
  const travelers =
    data?.flightBooking?.at?.(0)?.travelers ||
    data?.flightBooking?.travelers ||
    [];
  const list = Array.isArray(data?.passengerInfo) ? data.passengerInfo : [];
  return list.map((newData, index) => ({
    previous:
      travelers.find((t) => t?.id != null && newData?.id != null && t.id === newData.id) ||
      travelers.find(
        (t) => t?._id && newData?._id && String(t._id) === String(newData._id),
      ) ||
      travelers[index] ||
      null,
    newData,
  }));
}

/**
 * Compare previous vs asked-for passenger details.
 * Accepts either one pair `{ previous, newData }` or `{ passengers: [...] }`.
 */
export const ComparePAX = ({ data }) => {
  const passengers = useMemo(() => {
    if (Array.isArray(data?.passengers) && data.passengers.length)
      return data.passengers;
    if (data?.previous != null || data?.newData != null)
      return [{ previous: data.previous, newData: data.newData }];
    return [];
  }, [data]);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    setSelected(0);
  }, [passengers.length, data?.newData?.id, data?.passengers?.[0]?.newData?.id]);

  const entries = useMemo(
    () =>
      passengers.map((pair, index) => {
        const rows = rowsFor(pair?.previous, pair?.newData);
        const changedRows = rows.filter((r) => r.changed);
        return {
          index,
          label: passengerLabel(pair?.newData || pair?.previous, index),
          rows,
          changedRows,
          changedCount: changedRows.length,
        };
      }),
    [passengers],
  );

  const active = entries[selected] || entries[0];
  const passengerCount = entries.length;
  const totalChangedFields = entries.reduce(
    (sum, entry) => sum + entry.changedCount,
    0,
  );
  const passengersWithChanges = entries.filter((e) => e.changedCount > 0).length;

  const headerSub =
    passengerCount > 1
      ? `${passengerCount} passengers · ${totalChangedFields} field${
          totalChangedFields === 1 ? "" : "s"
        } changed`
      : `${active?.label || "Passenger"}${
          active?.changedCount > 0
            ? ` · ${active.changedCount} field${
                active.changedCount === 1 ? "" : "s"
              } changed`
            : " · no field diffs"
        }`;

  const badgeLabel =
    passengerCount > 1
      ? `${passengersWithChanges || passengerCount} passengers`
      : active?.changedCount > 0
        ? `${active.changedCount} changed`
        : null;

  if (!active) return null;

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
            Changed passenger details
          </div>
          <div className="text-[11px] text-[#6D7C94] truncate">{headerSub}</div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {badgeLabel ? (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-[#FFFBEB] text-[#92400E] border border-[#FEF3C7]">
              {badgeLabel}
            </span>
          ) : null}
          <span
            className={`text-[#6D7C94] text-xs transition-transform ${
              open ? "rotate-180" : ""
            }`}
          >
            ▼
          </span>
        </div>
      </button>

      {!open && totalChangedFields > 0 ? (
        <div className="mt-1.5 space-y-1.5">
          {passengerCount > 1 ? (
            <div className="text-[11px] text-[#6D7C94]">
              Correction requested for {passengerCount} passengers. Open to
              switch between them.
            </div>
          ) : null}
          {(passengerCount > 1
            ? entries.filter((e) => e.changedCount > 0).slice(0, 2)
            : [active]
          ).map((entry) => (
            <div key={entry.index} className="space-y-0.5">
              {passengerCount > 1 ? (
                <div className="text-[10px] font-semibold text-[#0F172A]">
                  {entry.label}
                  <span className="ml-1 font-normal text-[#6D7C94]">
                    · {entry.changedCount} field
                    {entry.changedCount === 1 ? "" : "s"}
                  </span>
                </div>
              ) : null}
              {entry.changedRows.slice(0, passengerCount > 1 ? 2 : 3).map((row) => (
                <div
                  key={`${entry.index}-${row.key}`}
                  className="flex items-center gap-2 text-xs text-[#0F172A] truncate"
                >
                  <span className="text-[#6D7C94] shrink-0 w-[72px] truncate">
                    {row.title}
                  </span>
                  <span className="text-[#94A3B8] line-through truncate">
                    {row.before}
                  </span>
                  <span className="text-[#94A3B8] shrink-0">→</span>
                  <span className="font-semibold text-[#92400E] truncate">
                    {row.after}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {passengerCount > 1 && passengersWithChanges > 2 ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="text-[11px] font-semibold text-primary1"
            >
              +{passengersWithChanges - 2} more passengers — view all
            </button>
          ) : active.changedCount > 3 && passengerCount === 1 ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="text-[11px] font-semibold text-primary1"
            >
              +{active.changedCount - 3} more — view all
            </button>
          ) : null}
        </div>
      ) : null}

      <CommonCollapsible open={open} duration={280}>
        <div className="pt-2">
          {passengerCount > 1 ? (
            <div className="flex flex-wrap gap-1.5 mb-2.5">
              {entries.map((entry) => {
                const on = entry.index === selected;
                return (
                  <button
                    key={entry.index}
                    type="button"
                    onClick={() => setSelected(entry.index)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                      on
                        ? "bg-sky-100 text-sky-900 border-sky-200"
                        : "bg-white text-[#0F172A] border-[#E8EEF5] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <span className="truncate max-w-[140px]">{entry.label}</span>
                    {entry.changedCount > 0 ? (
                      <span
                        className={`shrink-0 text-[9px] uppercase tracking-wide rounded px-1 py-0.5 ${
                          on
                            ? "bg-sky-200/80 text-sky-950"
                            : "bg-[#FEF3C7] text-[#B45309]"
                        }`}
                      >
                        {entry.changedCount}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="hidden sm:grid grid-cols-[110px_1fr_1fr] gap-2 px-0.5 pb-1">
            <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              Field
            </span>
            <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              Previous
            </span>
            <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              New
            </span>
          </div>

          <div className="rounded-lg border border-[#E8EEF5] divide-y divide-[#F1F5F9]">
            {active.rows.map((row) => (
              <div
                key={row.key}
                className={`grid grid-cols-1 sm:grid-cols-[110px_1fr_1fr] gap-0.5 sm:gap-2 px-2.5 py-1.5 text-sm ${
                  row.changed ? "bg-[#FFFBEB]/60" : "bg-white"
                }`}
              >
                <div className="text-[11px] font-medium text-[#6D7C94] sm:text-[#0F172A] sm:text-xs leading-tight pt-0.5">
                  {row.title}
                </div>
                <div
                  className={`text-xs sm:text-sm leading-tight truncate ${
                    row.changed
                      ? "text-[#94A3B8] line-through"
                      : "text-[#0F172A]"
                  }`}
                  title={String(row.before)}
                >
                  <span className="sm:hidden text-[10px] text-[#94A3B8] mr-1 no-underline">
                    Prev
                  </span>
                  {row.before}
                </div>
                <div
                  className={`text-xs sm:text-sm leading-tight truncate font-semibold ${
                    row.changed
                      ? "text-[#92400E]"
                      : "text-[#0F172A] font-medium"
                  }`}
                  title={String(row.after)}
                >
                  <span className="sm:hidden text-[10px] text-[#94A3B8] mr-1 font-normal">
                    New
                  </span>
                  {row.after}
                  {row.changed ? (
                    <span className="ml-1.5 inline-block text-[9px] font-semibold uppercase tracking-wide text-[#B45309] bg-[#FEF3C7] rounded px-1 py-0.5 align-middle">
                      Changed
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CommonCollapsible>
    </div>
  );
};
